/**
 * Human-readable labels and record counts for a Generate-with-AI job's raw
 * backend ``phase`` (``GenerationProgressMessage.phase``).
 *
 * Realtime runs move through ``understanding_the_task`` -> ``generating_samples``
 * -> ``checking_labels``. LABEL_ONLY runs that take the vendor batch path report
 * ``submitting_batch`` -> ``batch_processing`` -> ``saving_labels``; while the
 * provider processes the batch there are no per-record counts, so the bar
 * shows the truth (``0 / 1,200 processed · 1,200 remaining``) and the phase —
 * never an ETA or an estimate.
 */

export const GENERATION_JOB_PHASE_UNDERSTANDING = 'understanding_the_task';
export const GENERATION_JOB_PHASE_GENERATING = 'generating';
export const GENERATION_JOB_PHASE_GENERATING_SAMPLES = 'generating_samples';
export const GENERATION_JOB_PHASE_CHECKING_LABELS = 'checking_labels';
export const GENERATION_JOB_PHASE_SUBMITTING_BATCH = 'submitting_batch';
export const GENERATION_JOB_PHASE_BATCH_PROCESSING = 'batch_processing';
export const GENERATION_JOB_PHASE_SAVING_LABELS = 'saving_labels';

export const GENERATION_JOB_PHASE_LABELS = Object.freeze({
    [GENERATION_JOB_PHASE_UNDERSTANDING]: 'Understanding the task',
    [GENERATION_JOB_PHASE_GENERATING]: 'Generating examples',
    [GENERATION_JOB_PHASE_GENERATING_SAMPLES]: 'Generating examples',
    [GENERATION_JOB_PHASE_CHECKING_LABELS]: 'Checking labels',
    [GENERATION_JOB_PHASE_SUBMITTING_BATCH]: 'Submitting batch to provider',
    [GENERATION_JOB_PHASE_BATCH_PROCESSING]: 'Provider processing batch',
    [GENERATION_JOB_PHASE_SAVING_LABELS]: 'Saving labels',
});

const BATCH_GENERATION_PHASES = new Set([
    GENERATION_JOB_PHASE_SUBMITTING_BATCH,
    GENERATION_JOB_PHASE_BATCH_PROCESSING,
    GENERATION_JOB_PHASE_SAVING_LABELS,
]);

/** Phases whose label is already the title of the "Generating" step row. */
const GENERATING_ROW_PHASES = new Set([
    GENERATION_JOB_PHASE_GENERATING,
    GENERATION_JOB_PHASE_GENERATING_SAMPLES,
]);

/** Raw backend phase as a trimmed string, or null when absent / not a string. */
export function normalizeJobPhase(phase) {
    if (typeof phase !== 'string') return null;
    const trimmed = phase.trim();
    return trimmed || null;
}

/** True for the vendor-batch LABEL_ONLY phases. Unknown / missing phases are false. */
export function isBatchGenerationPhase(phase) {
    const normalized = normalizeJobPhase(phase);
    return normalized ? BATCH_GENERATION_PHASES.has(normalized) : false;
}

/**
 * Human label for a raw backend phase. Unknown phases return null (never a
 * raw token and never a crash), so callers can fall back to counts only.
 */
export function generationPhaseLabel(phase) {
    const normalized = normalizeJobPhase(phase);
    if (!normalized) return null;
    return GENERATION_JOB_PHASE_LABELS[normalized] || null;
}

function toCount(value) {
    const number = Number(value);
    if (!Number.isFinite(number) || number < 0) return 0;
    return Math.floor(number);
}

/** Records not yet processed: ``max(total - processed, 0)``. */
export function remainingPrompts(processedPrompts, totalPrompts) {
    return Math.max(toCount(totalPrompts) - toCount(processedPrompts), 0);
}

/**
 * ``"500 / 1,200 processed · 700 remaining"`` with locale separators. Returns null
 * when the total is unknown or zero so the UI never shows ``0 / 0``.
 *
 * "processed" is the backend's own counter (``processed_prompts``): rows the
 * run has handled so far, including rows whose label call failed (those are
 * reported in the terminal failure breakdown). It is deliberately not called
 * "labeled" because that would overstate success while a run is in flight.
 */
export function formatProcessedCounts({ processedPrompts, totalPrompts } = {}) {
    const total = toCount(totalPrompts);
    if (total <= 0) return null;
    const processed = toCount(processedPrompts);
    const remaining = remainingPrompts(processed, total);
    return (
        `${processed.toLocaleString('en-US')} / ${total.toLocaleString('en-US')} processed` +
        ` · ${remaining.toLocaleString('en-US')} remaining`
    );
}

/**
 * Subtitle for the active "Generating" / "Generating labels" step row:
 * the phase label (omitted when it would just repeat the row title) and the
 * processed / remaining counts. Null when there is nothing truthful to show.
 */
export function generationStepSubtitle({ jobPhase, processedPrompts, totalPrompts } = {}) {
    const phase = normalizeJobPhase(jobPhase);
    const phaseLabel = phase && !GENERATING_ROW_PHASES.has(phase) ? generationPhaseLabel(phase) : null;
    const counts = formatProcessedCounts({ processedPrompts, totalPrompts });
    const parts = [phaseLabel, counts].filter(Boolean);
    return parts.length ? parts.join(' · ') : null;
}
